/*LIBRARY*/
import { useEffect, useRef, useCallback } from "react";
import { connect } from "react-redux";
import ReactDOMServer from "react-dom/server";

/*COMPONENT*/
import IMAGE_FULL from "./IMAGE_FULL";

/*REDUX*/
import { set_value_properties } from "../../App/actions/propertiesActions";

/*PICTURE*/
import ICON_CIRCLE_JUAL from "../../Assets/jsx/ICON_CIRCLE_JUAL";
import ICON_CIRCLE_SEWA from "../../Assets/jsx/ICON_CIRCLE_SEWA";

/*CONST*/
const CLUSTERING_DEBOUNCE_MS = 100; //0 | 50 | 100
const DENSITY_W = 25;
const DENSITY_H = 25;
const COLLISION_PILL_W = 35;
const COLLISION_PILL_H = 35;

// Image dimensions (L3)
const L3_W = 50;
const L3_H = 70;

const COLLISION_RADIUS_CIRCLE = 20;
const status_penjualan = "150009cc-a4f4-4ba2-9b52-1ef9fc40ef9b";
const tipe_rumah = "b7db85eb-e51f-401f-b6e0-da6723c44dc2";
const image_key_1 = "d6fb8c1d-1333-41c2-b1a3-f7b0837a31db";
const image_key_2 = "6b583da9-8f37-4d4b-a22a-aa8090dcf245";
const address_key = "54f742d4-92f8-4c9f-aa64-7f4285d13871";

const HTML_CIRCLE_JUAL = ReactDOMServer.renderToString(<ICON_CIRCLE_JUAL />);
const HTML_CIRCLE_SEWA = ReactDOMServer.renderToString(<ICON_CIRCLE_SEWA />);

// ---------------------------------------------------------------------------
// Heatmap Constants & Buffer Config
// ---------------------------------------------------------------------------
const HEATMAP_OPACITY = 0.35;
const HEATMAP_MIN_HIDDEN = 1;
const HEATMAP_GRID_PX = 40;

const CANVAS_BUFFER = 500;
const MARKER_BUFFER = 150;

const HEATMAP_LAYERS = [
  { blur: 22, r: 28, fill: "#3b82f6", min_count: 1 }, // Blue
  { blur: 14, r: 20, fill: "#a855f7", min_count: 2 }, // Purple
  { blur: 8, r: 14, fill: "#ef4444", min_count: 4 }, // Red
];

// ---------------------------------------------------------------------------
// iOS Safari Optimization: Pre-rendered Radial Brushes
// ---------------------------------------------------------------------------

const hexToRgb = (hex) => {
  const bigint = parseInt(hex.replace("#", ""), 16);
  return `${(bigint >> 16) & 255}, ${(bigint >> 8) & 255}, ${bigint & 255}`;
};

const BRUSH_CACHE = HEATMAP_LAYERS.map((layer) => {
  const canvas = document.createElement("canvas");
  const size = (layer.r + layer.blur) * 2;
  canvas.width = size;
  canvas.height = size;

  const ctx = canvas.getContext("2d");
  const center = size / 2;

  const gradient = ctx.createRadialGradient(
    center,
    center,
    layer.r * 0.2,
    center,
    center,
    layer.r + layer.blur,
  );

  const rgb = hexToRgb(layer.fill);

  gradient.addColorStop(0, `rgba(${rgb}, 1)`);
  gradient.addColorStop(0.5, `rgba(${rgb}, 0.8)`);
  gradient.addColorStop(1, `rgba(${rgb}, 0)`);

  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(center, center, layer.r + layer.blur, 0, Math.PI * 2);
  ctx.fill();

  return { canvas, offset: center };
});

// --- CSS Animation Definition ---
const STYLE_CSS = `
  @keyframes popupGrow {
    0% {
      opacity: 0;
      transform: scale(0.1) translateY(10px);
    }
    100% {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;

// ---------------------------------------------------------------------------
// HTML Generators & Caches
// ---------------------------------------------------------------------------

const getCustomMarkerHTML = (status, isSelected, propertyType) => {
  const isSewa = status === "Disewa";
  const bgColor = isSewa ? "#0ca5eb" : "#E02E2E";
  const displayType = propertyType || "Property";

  const shadowStyle = isSelected
    ? `0 0 0 3px ${bgColor}, 0px 2px 4px rgba(0,0,0,0.3)`
    : `0px 2px 4px rgba(0,0,0,0.3)`;

  const homeIconSVG = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>`;
  const landIconSVG = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect></svg>`;
  const rukoIconSVG = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M4 21h16"></path><path d="M6 21v-9"></path><path d="M18 21v-9"></path><path d="M22 10 Q 20 12.5 18 10 T 14 10 T 10 10 T 6 10 T 2 10 V 5 H 22 Z"></path></svg>`;

  let selectedIconSVG = homeIconSVG;
  if (displayType === "Tanah") selectedIconSVG = landIconSVG;
  else if (displayType === "Ruko") selectedIconSVG = rukoIconSVG;

  return `
    <div style="
      position: relative; 
      display: flex; 
      flex-direction: column; 
      align-items: center; 
      transform-origin: bottom center; 
    ">
      <div style="
        width: 24px; 
        height: 24px; 
        background-color: ${bgColor}; 
        border: 2px solid white;
        border-radius: 50%; 
        display: flex; 
        align-items: center; 
        justify-content: center; 
        box-shadow: ${shadowStyle}; 
        transition: all 0.2s ease; 
        box-sizing: content-box; 
      ">
        ${selectedIconSVG}
      </div>
      <div style="
        width: 0; 
        height: 0; 
        border-left: 5px solid transparent; 
        border-right: 5px solid transparent; 
        border-top: 6px solid white; 
        margin-top: -1px;
        filter: drop-shadow(0px 2px 1px rgba(0,0,0,0.2));
      "></div>
    </div>
  `;
};

const l3HtmlCache = new Map();

const resolvePropertyMediaUrl = (feature) => {
  const props = feature.properties || {};
  return props[image_key_1] || props[image_key_2] || null;
};

const getCustomImageMarkerHTML = (feature, status, isSelected) => {
  const mediaUrl = resolvePropertyMediaUrl(feature);
  const cacheKey = `${mediaUrl}|${status}|${isSelected ? 1 : 0}`;

  if (l3HtmlCache.has(cacheKey)) return l3HtmlCache.get(cacheKey);

  const isSewa = status === "Disewa";
  const borderColor = isSewa ? "#0ca5eb" : "#E02E2E";

  const shadowStyle = isSelected
    ? `0 0 0 3px ${borderColor}, 0px 2px 4px rgba(0,0,0,0.3)`
    : `0px 2px 4px rgba(0,0,0,0.3)`;

  const innerContent = mediaUrl
    ? `<img src="${mediaUrl}" decoding="async" fetchpriority="low" style="width:100%;height:100%;object-fit:cover;border-radius:4px;" alt=""/>`
    : `<div style="width:100%;height:100%;background:#eee;border-radius:4px;"></div>`;

  const html = `
    <div style="
      position: relative; 
      display: flex; 
      flex-direction: column; 
      align-items: center; 
      transform-origin: bottom center; 
    ">
      <div style="
        width: 32px; 
        height: 48px; 
        background-color: #FFFFFF; 
        border: 2px solid ${borderColor};
        border-radius: 6px; 
        display: flex; 
        align-items: center; 
        justify-content: center; 
        box-shadow: ${shadowStyle}; 
        transition: all 0.2s ease; 
        box-sizing: content-box; 
      ">
        ${innerContent}
      </div>
      <div style="
        width: 0; 
        height: 0; 
        border-left: 6px solid transparent; 
        border-right: 6px solid transparent; 
        border-top: 8px solid ${borderColor}; 
        margin-top: -1px;
        filter: drop-shadow(0px 2px 1px rgba(0,0,0,0.2));
      "></div>
    </div>
  `;

  l3HtmlCache.set(cacheKey, html);
  return html;
};

// ---------------------------------------------------------------------------
// Canvas Heatmap helpers
// ---------------------------------------------------------------------------
const bucketPointsForLayers = (points) => {
  if (points.length === 0) return HEATMAP_LAYERS.map(() => []);

  const cells = new Map();
  for (let i = 0; i < points.length; i++) {
    const { x, y } = points[i];
    const col = Math.floor(x / HEATMAP_GRID_PX);
    const row = Math.floor(y / HEATMAP_GRID_PX);
    const key = `${col},${row}`;
    const existing = cells.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      cells.set(key, {
        x: (col + 0.5) * HEATMAP_GRID_PX,
        y: (row + 0.5) * HEATMAP_GRID_PX,
        count: 1,
      });
    }
  }

  return HEATMAP_LAYERS.map(({ min_count }) => {
    const layer_points = [];
    cells.forEach((cell) => {
      if (cell.count >= min_count) {
        layer_points.push({ x: cell.x, y: cell.y });
      }
    });
    return layer_points;
  });
};

const createHeatmapCanvas = (container) => {
  const canvas = document.createElement("canvas");
  canvas.className = "property-heatmap-canvas";

  canvas.style.cssText = [
    "position:absolute",
    `top:-${CANVAS_BUFFER}px`,
    `left:-${CANVAS_BUFFER}px`,
    "pointer-events:none",
    `opacity:${HEATMAP_OPACITY}`,
    "z-index:5",
    `transform-origin: ${CANVAS_BUFFER}px ${CANVAS_BUFFER}px`,
    "will-change: transform",
  ].join(";");

  const ctx = canvas.getContext("2d");
  container.appendChild(canvas);

  return { canvas, ctx };
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
const LAYER_PROPERTY_APP = ({
  layer,
  properties,
  map_object_string = "map_object",
  set_value_properties,
}) => {
  const {
    features_property_app,
    basemap_used,
    feature_property_selected,
    multi_property_selected,
    modal_property,
    modal_property_double,
    modal_image_preview,
    preview_image_url,
    preview_media_list = [],
    preview_media_index = 0,
    is_show_property = true,
    is_show_image = false, // Added image mode extraction
  } = properties;

  const map_object = layer[map_object_string];

  // --- REFS ---
  const markersRef = useRef({});
  const screenPointsRef = useRef([]);
  const popupRef = useRef(null);
  const lastZoomRef = useRef(null);

  const rafRef = useRef(null);
  const debounceRef = useRef(null);
  const isDraggingRef = useRef(false);

  // Touch tracking for double-tap zoom
  const touchStateRef = useRef({
    start: null,
    time: 0,
    is_multi: false,
    lastTapTime: 0,
    tapTimeout: null,
  });
  const isTouchModeRef = useRef(false);

  // Heatmap Refs
  const heatmapCanvasRef = useRef(null);
  const heatmapCtxRef = useRef(null);
  const heatmapAnchorRef = useRef(null);
  const prevShowPropertyRef = useRef(is_show_property);
  const prevShowImageRef = useRef(is_show_image);

  const selectedStateRef = useRef({
    single: feature_property_selected,
    multi: multi_property_selected,
  });

  const modalRef = useRef({
    single: modal_property,
    multi: modal_property_double,
  });

  // --- INJECT STYLES ON MOUNT ---
  useEffect(() => {
    const styleId = "custom-marker-animations-property";
    if (!document.getElementById(styleId)) {
      const styleSheet = document.createElement("style");
      styleSheet.id = styleId;
      styleSheet.innerText = STYLE_CSS;
      document.head.appendChild(styleSheet);
    }
  }, []);

  // --- CLEAR CACHE ON DATA UPDATE ---
  useEffect(() => {
    l3HtmlCache.clear();
  }, [features_property_app]);

  // --- POPUP POSITIONING LOGIC ---
  const updatePopupPosition = useCallback(() => {
    if (!popupRef.current) return;

    const isSingleOpen =
      modalRef.current.single && selectedStateRef.current.single;
    const isMultiOpen =
      modalRef.current.multi &&
      selectedStateRef.current.multi &&
      selectedStateRef.current.multi.length > 0;

    if (!isSingleOpen && !isMultiOpen) {
      popupRef.current.style.display = "none";
      return;
    }

    const selectedSingleId = isSingleOpen
      ? selectedStateRef.current.single._id
      : null;
    const selectedMultiId = isMultiOpen
      ? selectedStateRef.current.multi[0]._id
      : null;

    let targetP = null;
    for (const p of screenPointsRef.current) {
      if (!p.isVisible || p.isOffMarkerScreen) continue;
      if (selectedSingleId && p.feature._id === selectedSingleId) {
        targetP = p;
        break;
      }
      if (
        selectedMultiId &&
        p.hasStack &&
        p.stackedFeatures.length > 0 &&
        p.stackedFeatures[0]._id === selectedMultiId
      ) {
        targetP = p;
        break;
      }
    }

    if (targetP) {
      const status = targetP.feature.properties[status_penjualan];
      const isSewa = status === "Disewa";

      let targetType;
      if (targetP.isCrowded || targetP.hasStack) {
        targetType = isSewa ? "circle_sewa" : "circle_jual";
      } else if (is_show_image) {
        targetType = isSewa ? "image_sewa" : "image_jual";
      } else {
        targetType = isSewa ? "marker_sewa" : "marker_jual";
      }

      let yOffset = targetType.startsWith("circle")
        ? "calc(-100% - 35px)"
        : "calc(-100% - 10px)";

      popupRef.current.style.display = "block";
      const newTransform = `translate3d(${targetP.x.toFixed(1)}px, ${targetP.y.toFixed(1)}px, 0) translate(-50%, ${yOffset})`;
      if (popupRef.current._cachedTransform !== newTransform) {
        popupRef.current.style.transform = newTransform;
        popupRef.current._cachedTransform = newTransform;
      }
    } else {
      popupRef.current.style.display = "none";
    }
  }, [is_show_image]);

  // --- SYNC STATE REFS ---
  useEffect(() => {
    selectedStateRef.current = {
      single: feature_property_selected,
      multi: multi_property_selected,
    };
    modalRef.current = {
      single: modal_property,
      multi: modal_property_double,
    };

    updatePopupPosition();
  }, [
    feature_property_selected,
    multi_property_selected,
    modal_property,
    modal_property_double,
    updatePopupPosition,
  ]);

  // --- HANDLERS ---
  const on_close = useCallback(() => {
    set_value_properties({ key: "modal_property", value: false });
    set_value_properties({ key: "modal_property_double", value: false });
    set_value_properties({ key: "feature_property_selected", value: null });
    set_value_properties({ key: "multi_property_selected", value: null });
  }, [set_value_properties]);

  const on_close_image = useCallback(() => {
    set_value_properties({ key: "modal_image_preview", value: false });
    set_value_properties({ key: "preview_image_url", value: null });
    set_value_properties({ key: "preview_media_list", value: [] });
    set_value_properties({ key: "preview_media_index", value: 0 });
  }, [set_value_properties]);

  const on_click_image = useCallback(
    (url, mediaList = [], index = 0) => {
      if (url) {
        set_value_properties({ key: "preview_image_url", value: url });
        set_value_properties({
          key: "preview_media_list",
          value: mediaList.length > 0 ? mediaList : [url],
        });
        set_value_properties({ key: "preview_media_index", value: index });
        set_value_properties({ key: "modal_image_preview", value: true });
      }
    },
    [set_value_properties],
  );

  const on_click_single = useCallback(
    (feature) => {
      set_value_properties({
        key: "feature_property_selected",
        value: feature,
      });
      set_value_properties({ key: "multi_property_selected", value: null });
      set_value_properties({ key: "modal_property", value: true });
      set_value_properties({ key: "modal_property_double", value: false });

      set_value_properties({ key: "modal_activity", value: false });
      set_value_properties({ key: "modal_activity_double", value: false });
      set_value_properties({ key: "feature_activity_selected", value: null });
      set_value_properties({ key: "multi_activity_selected", value: null });
    },
    [set_value_properties],
  );

  const on_click_double = useCallback(
    (features) => {
      set_value_properties({ key: "feature_property_selected", value: null });
      set_value_properties({ key: "multi_property_selected", value: features });
      set_value_properties({ key: "modal_property_double", value: true });
      set_value_properties({ key: "modal_property", value: false });

      set_value_properties({ key: "modal_activity", value: false });
      set_value_properties({ key: "modal_activity_double", value: false });
      set_value_properties({ key: "feature_activity_selected", value: null });
      set_value_properties({ key: "multi_activity_selected", value: null });
    },
    [set_value_properties],
  );

  const on_click_empty = useCallback(() => {
    set_value_properties({ key: "feature_property_selected", value: null });
    set_value_properties({ key: "multi_property_selected", value: null });
    set_value_properties({ key: "modal_property", value: false });
    set_value_properties({ key: "modal_property_double", value: false });
  }, [set_value_properties]);

  // --- HEATMAP UPDATER ---
  const updateHeatmap = useCallback(
    (hidden_on_screen_points, container_width, container_height) => {
      const canvas = heatmapCanvasRef.current;
      const ctx = heatmapCtxRef.current;

      if (!canvas || !ctx) return;

      if (
        !is_show_property ||
        hidden_on_screen_points.length < HEATMAP_MIN_HIDDEN
      ) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        canvas.style.display = "none";
        return;
      }

      const paddedWidth = container_width + CANVAS_BUFFER * 2;
      const paddedHeight = container_height + CANVAS_BUFFER * 2;

      if (canvas.width !== paddedWidth || canvas.height !== paddedHeight) {
        canvas.width = paddedWidth;
        canvas.height = paddedHeight;
      } else {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }

      const layer_point_arrays = bucketPointsForLayers(hidden_on_screen_points);

      layer_point_arrays.forEach((pts, idx) => {
        if (pts.length === 0) return;

        const brush = BRUSH_CACHE[idx];

        for (let i = 0; i < pts.length; i++) {
          const { x, y } = pts[i];

          const bx = x + CANVAS_BUFFER;
          const by = y + CANVAS_BUFFER;

          ctx.drawImage(
            brush.canvas,
            Math.floor(bx - brush.offset),
            Math.floor(by - brush.offset),
          );
        }
      });

      canvas.style.display = "block";
    },
    [is_show_property],
  );

  // --- HEAVY LOGIC: CLUSTERING & RENDER ---
  const updateCollisionsAndRender = useCallback(() => {
    if (!map_object || !markersRef.current) return;

    const canvasObj = map_object.getCanvas();
    const canvasWidth = canvasObj.clientWidth;
    const canvasHeight = canvasObj.clientHeight;

    if (!is_show_property) {
      screenPointsRef.current.forEach((p) => {
        p.isVisible = false;
      });
      Object.values(markersRef.current).forEach((markerElement) => {
        if (markerElement) markerElement.style.display = "none";
      });
      updatePopupPosition();
      updateHeatmap([], canvasWidth, canvasHeight);
      return;
    }

    const selectedSingle = selectedStateRef.current.single;
    const selectedMulti = selectedStateRef.current.multi;

    let forceHeatmapUpdate = false;
    if (
      prevShowPropertyRef.current !== is_show_property ||
      prevShowImageRef.current !== is_show_image
    ) {
      forceHeatmapUpdate = true;
      prevShowPropertyRef.current = is_show_property;
      prevShowImageRef.current = is_show_image;
    }

    const currentZoom = map_object.getZoom();
    const pitch = map_object.getPitch();
    const bearing = map_object.getBearing();
    const is3D = pitch !== 0 || bearing !== 0;

    let isZoomChange = false;
    if (
      lastZoomRef.current === null ||
      Math.abs(lastZoomRef.current - currentZoom) > 0.05
    ) {
      isZoomChange = true;
      lastZoomRef.current = currentZoom;
    }

    let isSignificantPan = false;
    if (heatmapAnchorRef.current) {
      const currentAnchorPx = map_object.project(
        heatmapAnchorRef.current.lngLat,
      );
      const dx = Math.abs(currentAnchorPx.x - heatmapAnchorRef.current.px.x);
      const dy = Math.abs(currentAnchorPx.y - heatmapAnchorRef.current.px.y);
      if (dx > CANVAS_BUFFER * 0.6 || dy > CANVAS_BUFFER * 0.6) {
        isSignificantPan = true;
      }
    }

    const shouldUpdateHeatmap =
      isZoomChange ||
      forceHeatmapUpdate ||
      !heatmapAnchorRef.current ||
      isSignificantPan ||
      is3D;

    // 1. Projection and Offscreen Check
    screenPointsRef.current.forEach((p) => {
      const point = map_object.project(p.feature.geometry.coordinates);
      p.x = point.x;
      p.y = point.y;
      p.base_x = point.x;
      p.base_y = point.y;

      p.isOffScreen =
        p.x < -CANVAS_BUFFER ||
        p.x > canvasWidth + CANVAS_BUFFER ||
        p.y < -CANVAS_BUFFER ||
        p.y > canvasHeight + CANVAS_BUFFER;

      p.isOffMarkerScreen =
        p.x < -MARKER_BUFFER ||
        p.x > canvasWidth + MARKER_BUFFER ||
        p.y < -MARKER_BUFFER ||
        p.y > canvasHeight + MARKER_BUFFER;

      p.isCrowded = false;
      p.hasStack = false;
      p.stackedFeatures = [p.feature];
    });

    const onScreenPoints = screenPointsRef.current.filter(
      (p) => !p.isOffScreen,
    );
    const sortedPoints = [...onScreenPoints].sort((a, b) => a.y - b.y);

    // 2. Density Check
    for (let i = 0; i < sortedPoints.length; i++) {
      const p1 = sortedPoints[i];
      if (p1.isOffMarkerScreen) continue;

      let neighbors = 0;
      for (let j = 0; j < sortedPoints.length; j++) {
        if (i === j) continue;
        if (sortedPoints[j].isOffMarkerScreen) continue;

        const dx = Math.abs(p1.x - sortedPoints[j].x);
        const dy = Math.abs(p1.y - sortedPoints[j].y);
        if (dx < DENSITY_W && dy < DENSITY_H) neighbors++;
      }
      if (neighbors > 0) p1.isCrowded = true;
    }

    // 3. Collision Check
    const visibleSpots = [];
    const hidden_on_screen_points = [];
    const collisionOrder = [...sortedPoints].sort((a, b) => {
      if (a.isCrowded === b.isCrowded) return 0;
      return a.isCrowded ? 1 : -1;
    });

    const CURRENT_COLLISION_W = is_show_image ? L3_W : COLLISION_PILL_W;
    const CURRENT_COLLISION_H = is_show_image ? L3_H : COLLISION_PILL_H;

    collisionOrder.forEach((p) => {
      if (p.isOffMarkerScreen) {
        p.isVisible = false;
        hidden_on_screen_points.push({ x: p.x, y: p.y });
        return;
      }

      let isHidden = false;
      for (const spot of visibleSpots) {
        const dx = Math.abs(p.x - spot.x);
        const dy = Math.abs(p.y - spot.y);
        if (spot.isCrowded) {
          if (dx * dx + dy * dy < COLLISION_RADIUS_CIRCLE ** 2) {
            isHidden = true;
            spot.hasStack = true;
            spot.stackedFeatures.push(p.feature);
          }
        } else {
          if (dx < CURRENT_COLLISION_W && dy < CURRENT_COLLISION_H)
            isHidden = true;
        }
        if (isHidden) break;
      }
      p.isVisible = !isHidden;
      if (!isHidden) {
        visibleSpots.push(p);
      } else {
        hidden_on_screen_points.push({ x: p.x, y: p.y });
      }
    });

    // 4. DOM Update
    screenPointsRef.current.forEach((p) => {
      const markerId = `marker_${p.index}`;
      let markerElement = markersRef.current[markerId];

      if (!markerElement) {
        markerElement = document.createElement("div");
        markerElement.className = "mapboxgl-marker-s-property-community";
        markerElement.style.position = "absolute";
        markerElement.style.transition = "none";
        markerElement.style.willChange = "transform";
        markerElement.style.pointerEvents = "none";
        map_object.getContainer().appendChild(markerElement);
        markersRef.current[markerId] = markerElement;
      }

      if (!p.isVisible || p.isOffMarkerScreen) {
        if (markerElement.style.display !== "none")
          markerElement.style.display = "none";
        return;
      }

      if (markerElement.style.display !== "block")
        markerElement.style.display = "block";

      const status = p.feature.properties[status_penjualan];
      const propertyType = p.feature.properties[tipe_rumah];
      const isSewa = status === "Disewa";

      let targetType;
      if (p.isCrowded || p.hasStack)
        targetType = isSewa ? "circle_sewa" : "circle_jual";
      else if (is_show_image) targetType = isSewa ? "image_sewa" : "image_jual";
      else targetType = isSewa ? "marker_sewa" : "marker_jual";

      // Selection Logic
      const selectedSingleId = selectedSingle ? selectedSingle._id : null;
      const selectedMultiId =
        selectedMulti && selectedMulti.length > 0 ? selectedMulti[0]._id : null;
      const currentStackId =
        p.stackedFeatures && p.stackedFeatures.length > 0
          ? p.stackedFeatures[0]._id
          : null;

      let isSelected = false;
      if (p.hasStack) {
        if (
          selectedMultiId &&
          currentStackId &&
          selectedMultiId === currentStackId
        )
          isSelected = true;
      } else {
        if (selectedSingleId && p.feature._id === selectedSingleId)
          isSelected = true;
      }

      // Generate HTML
      let iconHTML = "";
      let offsetTransform = "translate(-50%, -100%)";
      let zIndex = "20";

      if (targetType === "circle_jual") {
        iconHTML = HTML_CIRCLE_JUAL;
        offsetTransform = "translate(-50%, -50%)";
        zIndex = "10";
      } else if (targetType === "circle_sewa") {
        iconHTML = HTML_CIRCLE_SEWA;
        offsetTransform = "translate(-50%, -50%)";
        zIndex = "10";
      } else {
        if (isSelected) {
          iconHTML = `<div style="display: none;"></div>`;
        } else if (is_show_image) {
          iconHTML = getCustomImageMarkerHTML(p.feature, status, false);
        } else {
          iconHTML = getCustomMarkerHTML(status, false, propertyType);
        }
      }

      if (isSelected) zIndex = "1";

      const mediaUrl = is_show_image ? resolvePropertyMediaUrl(p.feature) : "";
      const currentSelectedState = isSelected ? "true" : "false";
      const innerHash = `${targetType}-${currentSelectedState}-${mediaUrl}`;
      const prevHash = markerElement.getAttribute("data-hash");

      if (prevHash !== innerHash) {
        markerElement.innerHTML = `<div style="width:100%; height:100%;">${iconHTML}</div>`;
        markerElement.setAttribute("data-icon-type", targetType);
        markerElement.setAttribute("data-selected", currentSelectedState);
        markerElement.setAttribute("data-hash", innerHash);
        markerElement.style.zIndex = zIndex;
      }

      const newTransform = `translate3d(${p.x.toFixed(1)}px, ${p.y.toFixed(1)}px, 0) ${offsetTransform}`;
      if (markerElement._cachedTransform !== newTransform) {
        markerElement.style.transform = newTransform;
        markerElement._cachedTransform = newTransform;
      }
    });

    // 5. Update Heatmap View
    if (shouldUpdateHeatmap) {
      const centerLngLat = map_object.getCenter();
      const centerPx = map_object.project(centerLngLat);
      heatmapAnchorRef.current = {
        lngLat: centerLngLat,
        px: centerPx,
        zoom: currentZoom,
      };

      if (heatmapCanvasRef.current) {
        const transform = `translate3d(0px, 0px, 0) scale(1)`;
        if (heatmapCanvasRef.current._cachedTransform !== transform) {
          heatmapCanvasRef.current.style.transform = transform;
          heatmapCanvasRef.current._cachedTransform = transform;
        }
      }
      updateHeatmap(hidden_on_screen_points, canvasWidth, canvasHeight);
    } else if (heatmapAnchorRef.current && heatmapCanvasRef.current) {
      const startZoom = heatmapAnchorRef.current.zoom;
      const scale = Math.pow(2, currentZoom - startZoom);
      const currentAnchorPx = map_object.project(
        heatmapAnchorRef.current.lngLat,
      );
      const tx = currentAnchorPx.x - heatmapAnchorRef.current.px.x * scale;
      const ty = currentAnchorPx.y - heatmapAnchorRef.current.px.y * scale;

      const transform = `translate3d(${tx}px, ${ty}px, 0) scale(${scale})`;
      if (heatmapCanvasRef.current._cachedTransform !== transform) {
        heatmapCanvasRef.current.style.transform = transform;
        heatmapCanvasRef.current._cachedTransform = transform;
      }
    }

    updatePopupPosition();
  }, [
    map_object,
    is_show_property,
    is_show_image,
    updatePopupPosition,
    updateHeatmap,
  ]);

  // --- POSITION UPDATE ---
  const updatePositions = useCallback(() => {
    if (!map_object || !is_show_property) {
      rafRef.current = null;
      return;
    }

    const currentZoom = map_object.getZoom();
    const pitch = map_object.getPitch();
    const bearing = map_object.getBearing();
    const is3D = pitch !== 0 || bearing !== 0;

    const canvasObj = map_object.getCanvas();
    const canvasWidth = canvasObj.clientWidth;
    const canvasHeight = canvasObj.clientHeight;

    const hidden_on_screen_points = [];

    // 1. UPDATE MARKERS & RE-PROJECT ALL POINTS
    screenPointsRef.current.forEach((p) => {
      const coords = p.feature.geometry.coordinates;
      const point = map_object.project(coords);

      p.x = point.x;
      p.y = point.y;

      p.isOffScreen =
        p.x < -CANVAS_BUFFER ||
        p.x > canvasWidth + CANVAS_BUFFER ||
        p.y < -CANVAS_BUFFER ||
        p.y > canvasHeight + CANVAS_BUFFER;

      if (!p.isVisible && !p.isOffScreen) {
        hidden_on_screen_points.push({ x: p.x, y: p.y });
      }

      const markerElement = markersRef.current[`marker_${p.index}`];
      if (!markerElement || markerElement.style.display === "none") return;

      const currentType = markerElement.getAttribute("data-icon-type");
      let offsetTransform = "translate(-50%, -100%)";
      if (currentType === "circle_jual" || currentType === "circle_sewa") {
        offsetTransform = "translate(-50%, -50%)";
      }

      const newTransform = `translate3d(${point.x.toFixed(1)}px, ${point.y.toFixed(1)}px, 0) ${offsetTransform}`;
      if (markerElement._cachedTransform !== newTransform) {
        markerElement.style.transform = newTransform;
        markerElement._cachedTransform = newTransform;
      }
    });

    // 2. UPDATE HEATMAP CANVAS
    if (is3D) {
      // Natively redraw onto the canvas when rotating or pitching
      if (heatmapCanvasRef.current) {
        const transform = `translate3d(0px, 0px, 0) scale(1)`;
        if (heatmapCanvasRef.current._cachedTransform !== transform) {
          heatmapCanvasRef.current.style.transform = transform;
          heatmapCanvasRef.current._cachedTransform = transform;
        }
      }
      updateHeatmap(hidden_on_screen_points, canvasWidth, canvasHeight);

      // Keep tracking anchor to transition smoothly back to flat 2D panning later
      heatmapAnchorRef.current = {
        lngLat: map_object.getCenter(),
        px: map_object.project(map_object.getCenter()),
        zoom: currentZoom,
      };
    } else {
      const useFastMath = heatmapAnchorRef.current;
      if (useFastMath) {
        const scale = Math.pow(2, currentZoom - heatmapAnchorRef.current.zoom);
        const currentAnchorPx = map_object.project(
          heatmapAnchorRef.current.lngLat,
        );
        const tx = currentAnchorPx.x - heatmapAnchorRef.current.px.x * scale;
        const ty = currentAnchorPx.y - heatmapAnchorRef.current.px.y * scale;

        if (heatmapCanvasRef.current) {
          const transform = `translate3d(${tx}px, ${ty}px, 0) scale(${scale})`;
          if (heatmapCanvasRef.current._cachedTransform !== transform) {
            heatmapCanvasRef.current.style.transform = transform;
            heatmapCanvasRef.current._cachedTransform = transform;
          }
        }
      }
    }

    updatePopupPosition();
    rafRef.current = null;
  }, [map_object, is_show_property, updatePopupPosition, updateHeatmap]);

  // --- TRIGGER UPDATE ON SELECTION CHANGE OR COMMUNITY TOGGLE ---
  useEffect(() => {
    updateCollisionsAndRender();
  }, [
    feature_property_selected,
    multi_property_selected,
    is_show_property,
    is_show_image,
    updateCollisionsAndRender,
  ]);

  // --- MAIN INITIALIZATION EFFECT ---
  useEffect(() => {
    if (!map_object || !features_property_app) return;

    const initializePoints = () => {
      screenPointsRef.current = features_property_app.map((feature, index) => ({
        index,
        feature,
        x: 0,
        y: 0,
        base_x: 0,
        base_y: 0,
        isVisible: true,
        isOffScreen: false,
        isOffMarkerScreen: false,
        isCrowded: false,
        hasStack: false,
        stackedFeatures: [feature],
      }));
      heatmapAnchorRef.current = null;
    };

    if (
      screenPointsRef.current.length === 0 &&
      features_property_app.length > 0
    ) {
      initializePoints();
    }

    const container = map_object.getContainer();
    const existing_canvas = container.querySelector(".property-heatmap-canvas");
    if (!existing_canvas) {
      const { canvas, ctx } = createHeatmapCanvas(container);
      heatmapCanvasRef.current = canvas;
      heatmapCtxRef.current = ctx;
    } else {
      heatmapCanvasRef.current = existing_canvas;
      heatmapCtxRef.current = existing_canvas.getContext("2d");
    }

    // 2. Bounding-Box Hit Detection
    const checkHit = (point) => {
      if (
        map_object._activity_hit_time &&
        Date.now() - map_object._activity_hit_time < 50
      ) {
        return;
      }

      const candidates = [...screenPointsRef.current].reverse();
      let hitFeature = null;

      for (const p of candidates) {
        if (!p.isVisible || p.isOffMarkerScreen) continue;

        const dx = point.x - p.x;
        const dy = point.y - p.y;

        let targetType;
        if (p.isCrowded || p.hasStack) {
          targetType = "circle";
        } else if (is_show_image) {
          targetType = "image";
        } else {
          targetType = "marker";
        }

        let isHit = false;

        if (targetType === "image") {
          isHit = dx >= -L3_W / 2 && dx <= L3_W / 2 && dy >= -L3_H && dy <= 0;
        } else if (targetType === "marker") {
          isHit =
            dx >= -COLLISION_PILL_W / 2 &&
            dx <= COLLISION_PILL_W / 2 &&
            dy >= -COLLISION_PILL_H &&
            dy <= 0;
        } else {
          isHit = dx >= -20 && dx <= 20 && dy >= -20 && dy <= 20;
        }

        if (isHit) {
          hitFeature = p;
          break;
        }
      }

      if (hitFeature) {
        if (hitFeature.hasStack) on_click_double(hitFeature.stackedFeatures);
        else on_click_single(hitFeature.feature);
      } else {
        on_click_empty();
      }
    };

    const handleMapClick = (e) => {
      if (isTouchModeRef.current) {
        isTouchModeRef.current = false;
        return;
      }
      if (!isDraggingRef.current) checkHit(e.point);
    };

    const handleTouchStart = (e) => {
      isTouchModeRef.current = true;
      if (e.points && e.points.length > 1) {
        touchStateRef.current.is_multi = true;
      } else if (e.points && e.points.length === 1) {
        touchStateRef.current.is_multi = false;
        touchStateRef.current.start = e.point;
        touchStateRef.current.time = Date.now();
      }
    };

    const handleTouchEnd = (e) => {
      if (touchStateRef.current.is_multi) return;

      if (touchStateRef.current.start && e.point) {
        const dx = e.point.x - touchStateRef.current.start.x;
        const dy = e.point.y - touchStateRef.current.start.y;
        const dist_sq = dx * dx + dy * dy;
        const now = Date.now();
        const duration = now - touchStateRef.current.time;

        if (dist_sq < 100 && duration < 500) {
          const timeSinceLastTap = now - touchStateRef.current.lastTapTime;

          if (timeSinceLastTap < 300) {
            if (touchStateRef.current.tapTimeout) {
              clearTimeout(touchStateRef.current.tapTimeout);
              touchStateRef.current.tapTimeout = null;
            }
            touchStateRef.current.lastTapTime = now;
            return;
          }

          touchStateRef.current.lastTapTime = now;
          const hitPoint = { x: e.point.x, y: e.point.y };

          touchStateRef.current.tapTimeout = setTimeout(() => {
            checkHit(hitPoint);
            touchStateRef.current.tapTimeout = null;
          }, 300);
        }
      }
      touchStateRef.current.start = null;
    };

    // --- HYBRID MOVE LISTENER ---
    const onMapMove = () => {
      if (!rafRef.current) {
        rafRef.current = requestAnimationFrame(updatePositions);
      }

      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        updateCollisionsAndRender();
      }, CLUSTERING_DEBOUNCE_MS);
    };

    const onMoveStart = () => {
      isDraggingRef.current = true;
    };

    const onMoveEnd = () => {
      isDraggingRef.current = false;

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }

      updateCollisionsAndRender();
    };

    const attachAllListeners = () => {
      initializePoints();
      updateCollisionsAndRender();

      map_object.on("move", onMapMove);
      map_object.on("zoom", onMapMove);
      map_object.on("resize", onMapMove);
      map_object.on("movestart", onMoveStart);
      map_object.on("moveend", onMoveEnd);
      map_object.on("zoomend", onMoveEnd);
      map_object.on("click", handleMapClick);
      map_object.on("touchstart", handleTouchStart);
      map_object.on("touchend", handleTouchEnd);
    };

    const removeAllListeners = () => {
      map_object.off("move", onMapMove);
      map_object.off("zoom", onMapMove);
      map_object.off("resize", onMapMove);
      map_object.off("movestart", onMoveStart);
      map_object.off("moveend", onMoveEnd);
      map_object.off("zoomend", onMoveEnd);
      map_object.off("click", handleMapClick);
      map_object.off("touchstart", handleTouchStart);
      map_object.off("touchend", handleTouchEnd);

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      if (touchStateRef.current.tapTimeout) {
        clearTimeout(touchStateRef.current.tapTimeout);
        touchStateRef.current.tapTimeout = null;
      }
    };

    const handleStyleData = () => {
      if (map_object.isStyleLoaded()) {
        attachAllListeners();
        map_object.off("styledata", handleStyleData);
      }
    };

    if (map_object.isStyleLoaded()) attachAllListeners();
    else map_object.on("styledata", handleStyleData);

    return () => {
      map_object.off("styledata", handleStyleData);
      removeAllListeners();
      Object.values(markersRef.current).forEach((marker) => {
        if (marker.parentNode) marker.parentNode.removeChild(marker);
      });
      markersRef.current = {};

      if (heatmapCanvasRef.current && heatmapCanvasRef.current.parentNode) {
        heatmapCanvasRef.current.parentNode.removeChild(
          heatmapCanvasRef.current,
        );
      }
      heatmapCanvasRef.current = null;
      heatmapCtxRef.current = null;
      heatmapAnchorRef.current = null;
    };
  }, [
    map_object,
    features_property_app,
    basemap_used,
    is_show_image,
    updateCollisionsAndRender,
    updatePositions,
    on_click_single,
    on_click_double,
    on_click_empty,
  ]);

  // --- RENDER CONTENT ---
  const RenderContent = ({ feature }) => {
    const safeFeature = feature || {};
    const props = safeFeature.properties || {};
    const user = safeFeature.user || {};

    const img1 = props[image_key_1];
    const img2 = props[image_key_2];

    const medias = [img1, img2].filter(Boolean);

    const propertyStatus = props[status_penjualan];
    const isSewaBadge = propertyStatus === "Disewa";

    const profilePic =
      user?.profile_picture?.url_compressed || user?.profile_picture?.url || "";

    return (
      <div style={{ marginBottom: "16px" }}>
        <div style={{ display: "flex", gap: "10px", marginBottom: "8px" }}>
          {img1 && (
            <div style={{ flex: 1, width: "0px" }}>
              <img
                src={img1}
                alt="Property 1"
                onClick={() =>
                  on_click_image(img1, medias, medias.indexOf(img1))
                }
                style={{
                  width: "100%",
                  height: "auto",
                  aspectRatio: "1/1",
                  objectFit: "cover",
                  borderRadius: "8px",
                  display: "block",
                  cursor: "pointer",
                }}
              />
            </div>
          )}
          {img2 && (
            <div style={{ flex: 1, width: "0px" }}>
              <img
                src={img2}
                alt="Property 2"
                onClick={() =>
                  on_click_image(img2, medias, medias.indexOf(img2))
                }
                style={{
                  width: "100%",
                  height: "auto",
                  aspectRatio: "1/1",
                  objectFit: "cover",
                  borderRadius: "8px",
                  display: "block",
                  cursor: "pointer",
                }}
              />
            </div>
          )}
        </div>

        {propertyStatus && (
          <div style={{ marginBottom: "6px" }}>
            <span
              style={{
                backgroundColor: isSewaBadge ? "#0ca5eb" : "#E02E2E",
                color: "white",
                padding: "2px 8px",
                borderRadius: "4px",
                fontSize: "11px",
                fontWeight: "bold",
                textTransform: "uppercase",
                display: "inline-block",
              }}
            >
              {propertyStatus}
            </span>
          </div>
        )}

        <div
          style={{ fontSize: "14px", fontWeight: "bold", marginBottom: "4px" }}
        >
          {props[tipe_rumah] || "-"}
        </div>
        <div style={{ fontSize: "12px", color: "#555" }}>
          {props[address_key] || "-"}
        </div>

        {user && Object.keys(user).length > 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginTop: "12px",
              paddingTop: "12px",
              borderTop: "1px solid #f0f0f0",
            }}
          >
            {profilePic ? (
              <img
                src={profilePic}
                alt={user.full_name || "User profile"}
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  objectFit: "cover",
                  backgroundColor: "#eee",
                }}
              />
            ) : (
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  backgroundColor: "#ccc",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "14px",
                  color: "#fff",
                  fontWeight: "bold",
                }}
              >
                {(user.full_name || "U").charAt(0).toUpperCase()}
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column" }}>
              <span
                style={{
                  fontSize: "13px",
                  fontWeight: "bold",
                  color: "#333",
                  lineHeight: "1.2",
                }}
              >
                {user?.full_name || "Nama Pengguna"}
              </span>
              {user.name && (
                <span
                  style={{
                    fontSize: "11px",
                    color: "#777",
                    marginTop: "2px",
                  }}
                >
                  @{user.name}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <main
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        overflow: "hidden",
      }}
    >
      <div
        ref={popupRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          pointerEvents: "auto",
          display: "none",
          willChange: "transform",
        }}
      >
        <div
          style={{
            position: "relative",
            backgroundColor: "white",
            borderRadius: "15px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            width: "280px",
            transformOrigin: "bottom center",
            animation:
              "popupGrow 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards",
          }}
        >
          <button
            onClick={on_close}
            style={{
              position: "absolute",
              top: "8px",
              right: "8px",
              background: "white",
              border: "none",
              fontSize: "22px",
              cursor: "pointer",
              padding: "4px 8px",
              lineHeight: "1",
              color: "#333",
              zIndex: 10,
              borderRadius: "50%",
            }}
          >
            &times;
          </button>

          <div
            style={{
              maxHeight: "350px",
              overflowY: "auto",
              padding: "16px",
              paddingTop: "32px",
            }}
          >
            {modal_property && feature_property_selected && (
              <div>
                <RenderContent feature={feature_property_selected} />
              </div>
            )}

            {modal_property_double && multi_property_selected && (
              <div>
                {multi_property_selected.map((feature, idx) => (
                  <div
                    key={feature?._id || idx}
                    style={{
                      borderBottom: "1px solid #eee",
                      marginBottom: "16px",
                      paddingBottom: "16px",
                    }}
                  >
                    <h3
                      className="badge background_black margin_bottom"
                      style={{ fontSize: "14px", padding: "4px 8px" }}
                    >
                      Properti {idx + 1}
                    </h3>
                    <RenderContent feature={feature} />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div
            style={{
              position: "absolute",
              top: "100%",
              marginTop: "-1px",
              left: "50%",
              transform: "translateX(-50%)",
              width: "0",
              height: "0",
              borderLeft: "14px solid transparent",
              borderRight: "14px solid transparent",
              borderTop: "14px solid white",
              filter: "drop-shadow(0px 2px 2px rgba(0,0,0,0.1))",
            }}
          ></div>
        </div>
      </div>

      <IMAGE_FULL
        isOpen={modal_image_preview}
        mediaList={
          preview_media_list.length > 0
            ? preview_media_list
            : preview_image_url
              ? [preview_image_url]
              : []
        }
        initialIndex={preview_media_index}
        onClose={on_close_image}
      />
    </main>
  );
};

const mapStateToProps = (state) => ({
  layer: state.layer,
  properties: state.properties,
});

export default connect(mapStateToProps, { set_value_properties })(
  LAYER_PROPERTY_APP,
);
